'use client';

/**
 * The eyebrow above an H1 — rendered only when the page is not already saying it.
 *
 * Annette, 21 Oct 2026, of /insights: "this is messy dont know what this is about breadcrums and
 * then another???" — the page showed
 *
 *     Home · Insights
 *     Insights
 *     Insights from aXai
 *
 * The eyebrow was the site's "where you are" line, written before there were breadcrumbs. On
 * 20 Oct a trail went on every page ("i think there is supposed to be breadcrumbs on every page"),
 * and nothing took the eyebrow off — so every inner page named its location twice, and on
 * /insights and /contact the two lines were identical and adjacent. /what-we-do, /videos and /dsx
 * were worse: their eyebrows are little trails of their own ("Products · aXai Videos") sitting
 * under the real one.
 *
 * The same fault was fixed once before in this file's neighbour: PageIntro's comment records the
 * eyebrow repeating the H1 word for word on the legal pages, "which a screen reader reads out
 * twice before the page starts". This is that fault again, one line up.
 *
 * So: where a trail renders, the trail is the location and the eyebrow is dropped. Where no trail
 * renders — the homepage, and any route with no registered page — the eyebrow is the only thing
 * saying where the reader is, and it stays.
 */

import { usePathname } from 'next/navigation';
import { breadcrumbTrail } from '@/data/site-pages';
import { EYEBROW_CLASS } from './Page';

/** A single insight draws its own trail, which always begins Home · Insights. */
const INSIGHT = /^\/insights\/.+/;

export function hasTrail(pathname: string): boolean {
  if (INSIGHT.test(pathname)) return true;
  const crumbs = breadcrumbTrail(pathname);
  return Boolean(crumbs && crumbs.length > 1);
}

export function PageEyebrow({ children, className }: { children?: string; className?: string }) {
  const pathname = usePathname() || '/';
  if (!children || hasTrail(pathname)) return null;
  return <p className={className ?? `m-0 ${EYEBROW_CLASS}`}>{children}</p>;
}
